<template>
	<div class="t-bell" v-click-outside="close">
		<button class="t-bell-btn" :class="{open: open}" @click="toggle" :title="'התראות' + (unread ? ' (' + unread + ')' : '')">
			<span class="t-bell-icon">🔔</span>
			<span v-if="unread" class="t-bell-badge">{{ unread > 99 ? '99+' : unread }}</span>
		</button>

		<div v-if="open" class="t-bell-panel">
			<div class="t-bell-head">
				<b>התראות</b>
				<div class="t-bell-actions">
					<span v-if="unread" class="t-bell-link" @click="markAll">סמן הכל כנקרא</span>
					<span v-if="items.length" class="t-bell-link danger" @click="clearAll">נקה הכל</span>
				</div>
			</div>

			<div class="t-bell-body">
				<div v-if="!items.length" class="t-bell-empty">
					<div style="font-size:36px; opacity:.5;">🔕</div>
					<div style="margin-top:6px; color:var(--t-text-2);">אין התראות חדשות</div>
				</div>
				<div v-else>
					<div
						v-for="n in items"
						:key="n.id"
						class="t-bell-item"
						:class="{unread: !+n.is_read}"
						@click="clickItem(n)"
					>
						<div class="t-avatar sm" :style="{background: n.actor_color || '#0073ea'}">{{ initials(n.actor_name) }}</div>
						<div class="t-bell-text">
							<div class="t-bell-icon-type" :class="'tp-' + n.type">{{ typeIcon(n.type) }}</div>
							<div class="t-bell-msg">{{ n.message }}</div>
							<div class="t-bell-time">{{ relTime(n.created_at) }}</div>
						</div>
						<span class="t-bell-del" @click.stop="del(n.id)" title="מחק">×</span>
					</div>
				</div>
			</div>
		</div>
	</div>
</template>

<script>
import { TasksAPI, TasksRealtime, initials, formatDateTime } from "./TasksApi";

const clickOutside = {
	beforeMount(el, binding) {
		el._clickOutside = (e) => { if (!(el === e.target || el.contains(e.target))) binding.value(e); };
		document.addEventListener("click", el._clickOutside, true);
	},
	unmounted(el) { document.removeEventListener("click", el._clickOutside, true); },
};

export default {
	name: "NotificationsBell",
	props: { user: Object },
	directives: { "click-outside": clickOutside },
	data() {
		return {
			open: false,
			items: [],
			unread: 0,
			subscription: null,
		};
	},
	async mounted() {
		const promises = [this.refreshCount()];
		if (this.user && this.user.id) {
			promises.push(TasksRealtime.subscribe("tasks-user-" + this.user.id, {
				"notification.new": (d) => this.onNew(d.notification),
			}).then(s => { this.subscription = s; }));
		}
		await Promise.all(promises);
	},
	beforeUnmount() {
		if (this.subscription) TasksRealtime.unsubscribe(this.subscription);
	},
	methods: {
		initials, formatDateTime,
		typeIcon(t) {
			return ({
				assigned:       "👤",
				status_changed: "🔄",
				commented:      "💬",
				project_added:  "📁",
				mentioned:      "@",
			}[t] || "🔔");
		},
		relTime(d) {
			if (!d) return "";
			const dt = new Date(d.replace(" ", "T"));
			const diff = (Date.now() - dt.getTime()) / 1000;
			if (diff < 60)    return "לפני רגע";
			if (diff < 3600)  return "לפני " + Math.floor(diff/60) + " דק׳";
			if (diff < 86400) return "לפני " + Math.floor(diff/3600) + " שע׳";
			if (diff < 604800)return "לפני " + Math.floor(diff/86400) + " ימים";
			return formatDateTime(d);
		},
		async toggle() {
			this.open = !this.open;
			if (this.open) await this.loadList();
		},
		close() { this.open = false; },
		async refreshCount() {
			const res = await TasksAPI.notificationsCount();
			this.unread = +res?.unread || 0;
		},
		async loadList() {
			const res = await TasksAPI.notifications();
			this.items  = (res && res.items) || [];
			this.unread = +res?.unread || 0;
		},
		onNew(n) {
			if (!n) return;
			// dedup
			if (this.items.some(x => x.id == n.id)) return;
			this.items.unshift(n);
			if (this.items.length > 50) this.items.length = 50;
			if (!+n.is_read) this.unread++;
			// browser notification (best-effort)
			try {
				if (window.Notification && Notification.permission === "granted") {
					new Notification("TaskFlow", { body: n.message, icon: "/favicon.ico" });
				}
			} catch (_) {}
		},
		async clickItem(n) {
			if (!+n.is_read) {
				n.is_read = 1;
				this.unread = Math.max(0, this.unread - 1);
				await TasksAPI.notificationRead(n.id);
			}
			this.open = false;
			if (n.task_id) {
				// open task in current project view via custom event on the app
				const route = n.project_id ? "/tasks/project/" + n.project_id : "/tasks/all";
				this.$router.push(route).catch(() => {});
				// emit so a parent can open the modal
				this.$emit("open-task", +n.task_id);
			} else if (n.project_id) {
				this.$router.push("/tasks/project/" + n.project_id).catch(() => {});
			}
		},
		async markAll() {
			this.unread = 0;
			this.items = this.items.map(n => ({ ...n, is_read: 1 }));
			await TasksAPI.notificationsReadAll();
		},
		async clearAll() {
			if (!confirm("למחוק את כל ההתראות?")) return;
			this.items = [];
			this.unread = 0;
			await TasksAPI.notificationsClearAll();
		},
		async del(id) {
			this.items = this.items.filter(n => n.id != id);
			await TasksAPI.notificationDelete(id);
			await this.refreshCount();
		},
	},
};
</script>
